import type { OptionsDocument, OrderItem, QuoteDocument, QuoteOptions, QuoteSubstrate } from 'paintscout'
import striptags from 'striptags'
import type { QuoteItemConsumer, QuoteItemSection, RenderableItem, UpdateableProduct } from '../../../index'
import { generateSubstrateDescription } from '../../util/generate-substrate-description/generate-substrate-description'
import type { RenderableSubItem } from '../../items'
import { getItemSection } from '../../util/get-item-section'

interface OrderedSubstrate extends OrderItem, QuoteSubstrate {}

interface trimSubstrateInput {
  quote: QuoteDocument
  clientOptions: OptionsDocument
  item: OrderedSubstrate
  subItems: RenderableSubItem[]
  products?: UpdateableProduct[]
  areaIds: string[]
  options: QuoteOptions
  consumer: QuoteItemConsumer
  section: QuoteItemSection
  hours: number
  prep: number
  price: number
  noReportText?: boolean
  showGroup?: boolean
  groupKey?: string
}

// Convert a quoteSubstrate retrieved from quote.substrates to a Renderable Item to be edited/viewed.
export function trimSubstrate({
  quote,
  item,
  subItems,
  clientOptions,
  options,
  areaIds,
  consumer,
  section,
  noReportText,
  showGroup,
  groupKey,
  products = [],
  hours,
  prep,
  price
}: trimSubstrateInput): RenderableItem {
  const substrateSection = getItemSection(item)

  const label =
    substrateSection === section
      ? striptags(item?.client_label ? item.client_label : item?.label ?? '')
      : striptags(String(item.clientLabel?.default))
  const substrateKey = item.key

  const defaultDescription = generateSubstrateDescription({
    quote,
    substrateKey,
    options,
    clientOptions,
    section,
    noReportText,
    showGroup,
    groupKey
  })

  const description =
    consumer === 'crew'
      ? { useCustom: false, default: '', custom: '' }
      : {
          useCustom: substrateSection === section ? !!item.area_string.use_custom : false,
          custom: item.area_string.custom,
          default: defaultDescription
        }

  const { label: dimensionLabel, value: dimensionValue } = subItems.reduce(
    (acc, subItem) => {
      if (subItem.dimension) {
        return {
          label: subItem.dimension.label,
          value: acc.value + subItem.dimension.value
        }
      }

      return acc
    },
    {
      label: '',
      value: 0
    }
  )
  const name = dimensionValue
    ? `${label} (${
        Math.floor(dimensionValue * 100) === dimensionValue * 100 ? dimensionValue : dimensionValue.toFixed(2)
      }${dimensionLabel})`
    : label

  const r: RenderableItem = {
    key: item.key,
    type: item.type,
    name,
    description,
    files: item?.files ?? [],
    images: item?.images ?? [],
    value: price,
    subItems,
    ...(products?.length ? { products } : {})
  }

  if (consumer === 'crew') {
    r.additionalFields = {
      prep: prep,
      painting: hours,
      price,
      areaIds
    }
  } else {
    r.additionalFields = {
      painting: hours,
      prep: prep,
      price,
      areaIds
    }
  }
  return r
}
